/** @jsxImportSource @emotion/react */
'use memo'

import React, { createContext, useContext, useEffect, useReducer } from 'react'
import { useUsersMeSuspensions } from '@sportninja/common/hooks/useUsersMeSuspensions'
import {
  loadProfileItem,
  loadProfilePlayers,
} from '../../helpers/profileRequests'
import { ProfileIdentifier } from '../../types'
import { initialState, profileReducer } from './reducer'
import { ActionType, ProfileContextValue } from './types'

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined)

interface ProfileProviderProps {
  children: React.ReactNode
  selfUserId?: ProfileIdentifier
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({
  children,
  selfUserId,
}) => {
  const [state, dispatch] = useReducer(profileReducer, initialState)
  const {
    data: suspensions,
    loading: suspensionLoading,
    isSuspended,
    refetch,
  } = useUsersMeSuspensions(false)

  useEffect(() => {
    loadProfileItem(dispatch).catch(() => undefined)
    refetch().catch(() => undefined)
    loadProfilePlayers(dispatch).catch(() => undefined)
  }, [refetch, selfUserId])

  const value: ProfileContextValue = {
    ...state,
    suspensions,
    suspensionLoading,
    isSuspended,
    readItem: async (forceApi = false) => {
      await loadProfileItem(dispatch, forceApi)
    },
    loadPlayers: async () => {
      await loadProfilePlayers(dispatch)
    },
    refreshSuspensions: async () => {
      await refetch()
    },
    setShowStats: (nextShowStats) => {
      dispatch({ type: ActionType.SET_SHOW_STATS, payload: nextShowStats })
    },
    openNotificationSettings: () => {
      dispatch({
        type: ActionType.SET_NOTIFICATION_SETTINGS_OPEN,
        payload: true,
      })
    },
    closeNotificationSettings: () => {
      dispatch({
        type: ActionType.SET_NOTIFICATION_SETTINGS_OPEN,
        payload: false,
      })
    },
    openEditModal: () => {
      dispatch({ type: ActionType.SET_EDIT_OPEN, payload: true })
    },
    closeEditModal: () => {
      dispatch({ type: ActionType.SET_EDIT_OPEN, payload: false })
    },
  }

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  )
}

export const useProfileContext = (): ProfileContextValue => {
  const context = useContext(ProfileContext)

  if (!context) {
    throw new Error('useProfileContext must be used within a ProfileProvider')
  }

  return context
}
